/* I&C · motion layer · Our promise (rd-promise.liquid)
   Top rule and column dividers are drawn by a travelling gold light that cools to the hair colour; each roman numeral is
   hand-drawn as italic SVG strokes (any other numeral text gets a gold ink wipe); titles and text rise in stagger; a warm
   vignette drifts behind the columns and leans toward the hovered column. Single IIFE, no globals, ES2018;
   one setup per .promise__cols, guarded by data-rd-m-promise on its section.
   Does nothing under prefers-reduced-motion. */
(function () {
  'use strict';
  var MQ_REDUCE = '(prefers-reduced-motion: reduce)';
  var MQ_POINTER = '(hover: hover) and (pointer: fine)';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  /* timing (ms): per-column stagger and the start of each layer inside a column */
  var COL = 240, T_VLINE = 320, T_NUM = 520, T_STROKE = 220, T_H3 = 760, T_P = 930, TAIL = 1300;
  /* italic stroke alphabet in a 24-unit tall box: advance width, stroke paths (absolute coords), dots [x, y] */
  var GLYPHS = {
    i: { w: 9.5, s: ['M2.2 11.2C3.6 9.6 5.4 8.8 7 9.1L4.6 19.8C4.3 21.2 5 22 6.3 21.4C7.2 21 8 20.2 8.8 19.3'], d: [[8.4, 4.6]] },
    v: { w: 12.5, s: ['M1.4 9.6C3 9.4 4 10.4 4.5 12.2L6.6 21.6C8.4 19 10.2 15.4 12 9.4'], d: [] },
    x: { w: 12.5, s: ['M1.6 9.4C3.6 10.4 6.6 16.4 10.8 22', 'M11.6 9.2C8.6 12 5 17.8 1.2 21.8'], d: [] },
    '.': { w: 4.5, s: [], d: [[2, 21]] },
    ' ': { w: 4, s: [], d: [] }
  };

  function init() {
    var all = document.querySelectorAll('.rd .promise__cols');
    if (!all.length) return;
    if (!window.matchMedia || window.matchMedia(MQ_REDUCE).matches) return;
    Array.prototype.forEach.call(all, setup);
  }

  function setup(cols) {
    var section = cols.closest('.section') || cols.closest('.rd');
    if (!section || section.hasAttribute('data-rd-m-promise')) return;
    var items = Array.prototype.slice.call(cols.querySelectorAll('.promise__col'));
    if (!items.length) return;
    section.setAttribute('data-rd-m-promise', '');
    section.classList.add('rd-m-promise');

    /* ---- 1. warm vignette behind everything ---- */
    var vg = document.createElement('span');
    vg.className = 'rd-m-promise__vg';
    vg.setAttribute('aria-hidden', 'true');
    vg.appendChild(document.createElement('i'));
    vg.appendChild(document.createElement('i'));
    section.insertBefore(vg, section.firstChild);

    /* ---- 2. drawn rules: the top rule across the block, a divider before every column but the first ---- */
    cols.appendChild(line('rd-m-promise__rule', 0));

    /* ---- 3. per column: divider, numeral, staggered title + text delays ---- */
    items.forEach(function (col, i) {
      var base = i * COL;
      if (i > 0) col.insertBefore(line('rd-m-promise__vline', T_VLINE + base), col.firstChild);
      var num = col.querySelector('.promise__num');
      if (num && !num.querySelector('.rd-m-promise__txt')) drawNumeral(num, T_NUM + base);
      var h3 = col.querySelector('h3'), p = col.querySelector('p');
      if (h3) h3.style.setProperty('--rd-d', ms(T_H3 + base));
      if (p) p.style.setProperty('--rd-d', ms(T_P + base));
    });
    var settleAt = T_P + (items.length - 1) * COL + TAIL;

    /* ---- 4. entrance + on-screen gating ---- */
    var started = false;
    function start() {
      if (started) return;
      started = true;
      section.classList.add('rd-m-in');
      setTimeout(function () { section.classList.add('rd-m-settled'); }, settleAt);
    }
    if ('IntersectionObserver' in window) {
      var ioIn = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { start(); ioIn.disconnect(); } });
      }, { threshold: 0.18, rootMargin: '0px 0px -6% 0px' });
      ioIn.observe(cols);
      var ioOn = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { section.classList.toggle('rd-m-on', en.isIntersecting); });
      }, { threshold: 0 });
      ioOn.observe(section);
    } else {
      start();
      section.classList.add('rd-m-on');
    }

    /* ---- 5. pointer: the vignette leans toward the hovered column (desktop only) ---- */
    if (window.matchMedia(MQ_POINTER).matches) {
      var centers = null;
      function measure() {
        var s = section.getBoundingClientRect(), mid = s.left + s.width / 2;
        centers = items.map(function (c) { var r = c.getBoundingClientRect(); return r.left + r.width / 2 - mid; });
      }
      window.addEventListener('resize', function () { centers = null; }, { passive: true });
      items.forEach(function (col, i) {
        col.addEventListener('pointerenter', function () {
          if (!centers) measure();
          section.style.setProperty('--rd-vgx', Math.round(centers[i] * 0.55) + 'px');
        }, { passive: true });
      });
      cols.addEventListener('pointerleave', function () { section.style.setProperty('--rd-vgx', '0px'); }, { passive: true });
    }
  }

  /* a rule element whose ::before (hair) and ::after (gold light) are animated in CSS */
  function line(cls, delay) {
    var el = document.createElement('span');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    el.style.setProperty('--rd-d', ms(delay));
    return el;
  }

  /* keeps the original numeral text for assistive tech, adds a stroke-drawn SVG (or a wipe class) beside it */
  function drawNumeral(num, delay) {
    var text = (num.textContent || '').trim();
    var txt = document.createElement('span');
    txt.className = 'rd-m-promise__txt';
    txt.textContent = text;
    while (num.firstChild) num.removeChild(num.firstChild);
    num.appendChild(txt);
    var svg = text && buildGlyph(text, delay);
    if (svg) {
      num.classList.add('rd-m-promise__num--svg');
      num.appendChild(svg);
    } else {
      num.classList.add('rd-m-promise__num--wipe');
      txt.style.setProperty('--rd-d', ms(delay));
    }
  }

  function buildGlyph(text, delay) {
    var chars = text.toLowerCase().replace(/\s+/g, ' ').split(''), x = 1, k = 0, groups = [];
    for (var i = 0; i < chars.length; i++) {
      var g = GLYPHS[chars[i]];
      if (!g) return null;
      var grp = document.createElementNS(SVG_NS, 'g');
      grp.setAttribute('transform', 'translate(' + x + ' 0)');
      g.s.forEach(function (d) {
        var path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('d', d);
        path.setAttribute('pathLength', '1');
        path.style.setProperty('--rd-d', ms(delay + (k++) * T_STROKE));
        grp.appendChild(path);
      });
      g.d.forEach(function (pt) {
        var c = document.createElementNS(SVG_NS, 'circle');
        c.setAttribute('cx', pt[0]); c.setAttribute('cy', pt[1]); c.setAttribute('r', '1.15');
        c.style.setProperty('--rd-d', ms(delay + k * T_STROKE + 120));
        grp.appendChild(c);
      });
      groups.push(grp);
      x += g.w;
    }
    var w = x + 1, svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'rd-m-promise__glyph');
    svg.setAttribute('viewBox', '0 0 ' + w + ' 24');
    svg.setAttribute('width', (w * 22 / 24).toFixed(1));
    svg.setAttribute('height', '22');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    groups.forEach(function (g) { svg.appendChild(g); });
    return svg;
  }

  function ms(n) { return (n / 1000) + 's'; }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
